import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Book, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import FullPageLayout from '@/components/layout/FullPageLayout';

// Define interface for invitation validation response
interface InvitationValidationResult {
  valid: boolean;
  message: string;
  organizationId?: string;
  organizationName?: string;
  expiresAt?: string;
  data?: any;
}

const invitationSignupSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  fullName: z.string().min(2, { message: 'Please enter your full name' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
  confirmPassword: z.string().min(6, { message: 'Confirm password must be at least 6 characters' }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type InvitationSignupFormValues = z.infer<typeof invitationSignupSchema>;

const InvitationSignup: React.FC = () => {
  const { validateInvitationKey, completeRegistration } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [invitationKey, setInvitationKey] = useState<string>('');
  const [keyValidated, setKeyValidated] = useState<boolean>(false);
  const [orgInfo, setOrgInfo] = useState<{ id: string; name: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<InvitationSignupFormValues>({
    resolver: zodResolver(invitationSignupSchema),
    defaultValues: {
      email: '',
      fullName: '',
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    // Extract invitation key and email from URL
    const searchParams = new URLSearchParams(location.search);
    const key = searchParams.get('key');
    const emailParam = searchParams.get('email');

    // Prefill email if provided in query string
    if (emailParam) {
      form.setValue('email', emailParam);
    }

    if (key) {
      setInvitationKey(key);
      validateKey(key);
    }
  }, [location, form]);

  const validateKey = async (key: string) => {
    setIsValidating(true);
    try {
      const result = await validateInvitationKey(key) as InvitationValidationResult;
      
      if (!result) {
        console.error('No result returned from validateInvitationKey');
        toast.error('Failed to validate invitation key - no response data');
        setTimeout(() => navigate('/login'), 1500);
        return;
      }
      
      if (result.valid === true) {
        setKeyValidated(true);
        
        // The server response has organizationId and organizationName directly in the result
        // rather than in a nested data object
        if (!result.organizationId) {
          console.error('Missing organizationId in valid response:', result);
          toast.error('Invalid invitation format. Missing organization information.');
          setTimeout(() => navigate('/login'), 1500);
          return;
        }
        
        // Use the organizationId from the response
        const orgInfo = {
          id: result.organizationId,
          name: result.organizationName || 'Your Organization'
        };
        
        // Set state AFTER we've validated everything
        setOrgInfo(orgInfo);
        toast.success('Invitation validated successfully');
      } else {
        toast.error(typeof result.message === 'string' ? result.message : 'Invalid invitation key');
        // Navigate after a brief delay to allow toast to be seen
        setTimeout(() => navigate('/login'), 1500);
      }
    } catch (error) {
      console.error('Error validating key:', error);
      toast.error('Failed to validate invitation key');
      setTimeout(() => navigate('/login'), 1500);
    } finally {
      setIsValidating(false);
    }
  };

  const onSubmit = async (values: InvitationSignupFormValues) => {
    if (!keyValidated) {
      toast.error('Please validate your invitation key first');
      return;
    }
    
    if (!invitationKey) {
      console.error('Missing invitation key');
      toast.error('Invitation information is incomplete. Please try again with a valid invitation.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await completeRegistration(
        values.email,
        values.password,
        values.fullName,
        invitationKey
      );

      if (result && result.success) {
        toast.success('Account created successfully!');
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'Failed to create account. Please try again.');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error(error.message || 'There was a problem creating your account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualKeySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (invitationKey.trim()) {
      validateKey(invitationKey.trim());
    } else {
      toast.error('Please enter an invitation key');
    }
  };

  return (
    <FullPageLayout showBackButton={true} backTo="/login" backLabel="Back to Login">
      <div className="flex min-h-screen items-center justify-center px-4 py-12">
        <Card className="mx-auto w-full max-w-md animate-scale-in border-gray-8 relative z-10">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center">
              <div className="mb-4 inline-flex items-center space-x-2">
                <Book className="h-6 w-6 text-yellow" />
                <span className="text-xl font-semibold text-yellow">C TWO</span>
              </div>
            </div>
            <CardTitle className="text-2xl">Create an account</CardTitle>
            <CardDescription>
              {isValidating ? (
                'Validating your invitation...'
              ) : keyValidated && orgInfo ? (
                <>Join <span className="font-medium">{orgInfo.name}</span> with your invitation</>
              ) : (
                'Please enter your invitation key'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isValidating ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-gray-4">Validating invitation key...</p>
              </div>
            ) : keyValidated ? (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input 
                            {...field} 
                            type="email" 
                            placeholder="your.email@example.com" 
                            className="bg-gray-9 border-gray-7"
                            autoComplete="email"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="fullName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name</FormLabel>
                        <FormControl>
                          <Input 
                            {...field} 
                            placeholder="John Doe" 
                            className="bg-gray-9 border-gray-7"
                            autoComplete="name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input 
                              {...field} 
                              type={showPassword ? "text" : "password"} 
                              placeholder="••••••••" 
                              className="bg-gray-9 border-gray-7"
                              autoComplete="new-password"
                            />
                            <button 
                              type="button"
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-5 hover:text-gray-3"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input 
                              {...field} 
                              type={showConfirmPassword ? "text" : "password"} 
                              placeholder="••••••••" 
                              className="bg-gray-9 border-gray-7"
                              autoComplete="new-password"
                            />
                            <button 
                              type="button"
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-5 hover:text-gray-3"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-yellow text-black hover:bg-yellow-light"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating account...' : 'Create account'}
                  </Button>
                </form>
              </Form>
            ) : (
              <div className="space-y-4">
                <Alert className="mb-4">
                  <AlertDescription>
                    Enter the invitation key you received to create your account.
                  </AlertDescription>
                </Alert>
                
                <form onSubmit={handleManualKeySubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="invitation-key" className="text-sm font-medium">
                      Invitation Key
                    </label>
                    <div className="flex gap-2">
                      <Input
                        id="invitation-key"
                        value={invitationKey}
                        onChange={(e) => setInvitationKey(e.target.value)}
                        placeholder="Enter your invitation key"
                        className="bg-gray-9 border-gray-7"
                      />
                      <Button type="submit" disabled={isValidating}>
                        {isValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Validate"}
                      </Button>
                    </div>
                  </div>
                </form>
                
                <div className="text-center text-sm text-muted-foreground pt-4">
                  <p>
                    Have an account already?{' '}
                    <Link to="/login" className="text-yellow hover:underline font-medium">
                      Log in
                    </Link>
                  </p>
                  
                  <p className="mt-2">
                    Need an invitation?{' '}
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-yellow hover:underline font-medium"
                      onClick={() => toast.info('Please contact your administrator to request an invitation.')}
                    >
                      Request one
                    </Button>
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </FullPageLayout>
  );
};

export default InvitationSignup;
